import { useEffect, useState } from 'react';

import { Formik, Form } from 'formik';
import * as yup from 'yup';

import { api, useAPIContext } from '../../../api/context';
import { useFPContext } from '../../../utils/fingerprint';
import toast from '../../../utils/toast';
import review from '../../../components/review';
import Base from '../../Base';
import styles from './ReviewLinks.module.scss';

const ReviewLinks = ({ campaign, activity, handle }) => {

  const { token } = useAPIContext();

  /*
   * data
   */

  const [ dataLoading, setDataLoading ] = useState(true);
  const [ data, setData ] = useState();

  useEffect(() => {

    api(token).v1.campaigns.activities
      .obtain({
        campaign: campaign.id,
        activity: activity.id,
      })
      .then(res => {
        setDataLoading(false);
        setData(res.body.data);
      })
      .catch(err => {
        setDataLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token, campaign.id, activity.id]);

  /*
   * submit
   */

  const { fp } = useFPContext();

  const [ submitLoading, setSubmitLoading ] = useState(false);

  const submit = (values) => {

    setSubmitLoading(true);

    fp.get().then(result => {

      api(token).v1.campaigns.activities.submit
        .post(
          {
            campaign: campaign.id,
            activity: activity.id,
          },
          {
            data_json: JSON.stringify(values),
            fingerprint: result.visitorId,
          },
        )
        .then(res => {
          setSubmitLoading(false);

          if (res.status === 200) {
            handle(res.body);
            toast.show(toast.KIND.SUCCESS, 'Review saved!');
          }

          if (res.status === 400) {
            toast.show(toast.KIND.ERROR, res.body);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          toast.show(toast.KIND.WARNING, err);
        });
    });
  };

  /*
   * content
   */

  const loading = dataLoading || submitLoading;

  return (
    <Base title='Review'>
      <div className={styles.root}>
        {
          dataLoading &&
          <div className={styles.loading}>
            Loading...
          </div>
        }

        {
          data && (
            <>
              <div
                className={styles.instr}
                dangerouslySetInnerHTML={{
                  __html: data.instr
                }}
              />

              <Formik
                initialValues={{
                  stars: (data.result.data || {}).stars || 0,
                  text: (data.result.data || {}).text || '',
                  ...(data.config.custom_field_enabled && {
                    custom_field: (data.result.data || {}).custom_field || '',
                  }),
                }}
                validationSchema={yup.object({
                  stars: yup.number()
                    .min(1, 'The star rating is required.')
                    .max(5, 'The star rarting is invalid.'),
                  text: yup.string().required(),
                  ...(data.config.custom_field_enabled && data.config.custom_field_required && {
                    custom_field: yup.string().required(`${data.config.custom_field_label} is required.`),
                  }),
                })}
                validateOnChange={false}
                onSubmit={(values, actions) => {

                  if (loading) {
                    return;
                  }

                  submit(values);

                }} >

                {
                  ({ values, handleChange }) => (

                    <Form>

                      <review.Stars
                        className={styles.stars}
                        loading={loading}
                        count={5}
                      />

                      <review.Text
                        className={styles.text}
                        loading={loading}
                        label={(
                          <>
                            Review
                            {
                              data.config.prefix && (
                                <span>
                                  (Disclosure text added
                                  - DO NOT REMOVE THIS TEXT
                                  - You must include it when posting.)
                                </span>
                              )
                            }
                          </>
                        )}
                        prefix={data.config.prefix}
                        length={{
                          min: data.config.min_length,
                          max: data.config.max_length,
                        }}
                        nlp={{
                          include: data.config.nlp_include,
                          exclude: data.config.nlp_exclude,
                          exclude_default: data.config.nlp_exclude_default,
                        }}
                      />

                      {data.config.custom_field_enabled && (
                        <div className={styles.field}>
                          <label htmlFor='custom_field'>
                            {data.config.custom_field_label}
                          </label>
                          <input
                            id='custom_field'
                            name='custom_field'
                            type='text'
                            disabled={loading}
                            value={values.custom_field || ''}
                            onChange={handleChange}
                          />
                        </div>
                      )}

                      <button
                        key={1}
                        type='submit'
                        disabled={loading}
                        onClick={() => {
                          navigator.clipboard.writeText(values.text).catch(() => {
                            toast.show(toast.KIND.WARNING, 'Could not copy to clipboard. Please copy manually.');
                          });
                        }}>

                        1. Copy and Save Review

                      </button>

                      {
                        (data.config.review_links || []).map((item, i) => (
                          <a
                            key={i + 2}
                            target='_blank'
                            rel='noreferrer'
                            href={item.link}
                            disabled={loading}
                            className={`${loading ? styles.disabled : ''}`} >

                            {i + 2}. Paste it on {item.name}

                          </a>
                        ))
                      }

                    </Form>
                  )
                }
              </Formik>
            </>
          )
        }
      </div>
    </Base>
  );
};

export default ReviewLinks;